import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import UserSideNav from "../../components/user/UserSideNav";
import UserHeader from "../../components/user/UserHeader";
import { AlertCircle, CalendarClock, CheckCircle2, CreditCard, Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { paymentsApi } from "../../services/api";

function formatDate(value) {
  return value
    ? new Date(value).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "Not available";
}

export default function Subscription() {
  const { user, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(user?.subscriptionStatus || "none");
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(Boolean(user?.subscriptionCancelAtPeriodEnd));
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState(user?.subscriptionCurrentPeriodEnd || null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    const checkout = searchParams.get("checkout");
    const sessionId = searchParams.get("session_id");

    if (checkout === "cancelled") {
      setNotice("Subscription checkout was cancelled. No recurring charge was created.");
      setSearchParams({}, { replace: true });
    } else if (checkout === "success") {
      setNotice("Confirming your subscription with Stripe...");
    }

    paymentsApi.getSubscriptionStatus(sessionId)
      .then(async ({ data }) => {
        setStatus(data.data.status);
        setCancelAtPeriodEnd(Boolean(data.data.cancelAtPeriodEnd));
        setCurrentPeriodEnd(data.data.currentPeriodEnd);
        if (checkout === "success") {
          setNotice(data.data.status === "active"
            ? "Weekly Auto-Entry is active."
            : "Stripe is still confirming the subscription payment.");
        }
        await refreshUser();
      })
      .catch((err) => setError(err.response?.data?.message || "Unable to load subscription status."))
      .finally(() => {
        setLoading(false);
        if (checkout) setSearchParams({}, { replace: true });
      });
  }, []);

  async function startSubscription() {
    setWorking(true);
    setError("");
    try {
      const { data } = await paymentsApi.createSubscriptionCheckout();
      window.location.assign(data.data.url);
    } catch (err) {
      setError(err.response?.data?.message || "Unable to open subscription checkout.");
      setWorking(false);
    }
  }

  async function openBillingPortal() {
    setWorking(true);
    setError("");
    try {
      const { data } = await paymentsApi.createBillingPortal();
      window.location.assign(data.data.url);
    } catch (err) {
      setError(err.response?.data?.message || "Unable to open billing management.");
      setWorking(false);
    }
  }

  const isActive = status === "active";
  const needsAttention = status === "past_due";

  return (
    <div className="user-shell">
      <UserSideNav />
      <div className="user-app">
        <UserHeader />
        <main className="user-main">
          <div className="welcome-block">
            <div>
              <div className="page-kicker">Subscription</div>
              <h1>Manage Subscription</h1>
              <p>Control your weekly auto-entry and manage billing securely through Stripe.</p>
            </div>
          </div>

          {error && <div className="auth-error"><AlertCircle size={15} /> {error}</div>}
          {notice && <div className="auth-success">{notice}</div>}

          <section className="section-block" style={{ maxWidth: 560 }}>
            <div className="section-heading">
              <h2><ShieldCheck size={20} /> Current Plan</h2>
            </div>
            <article className="subscription-card">
              <span className={`status-pill ${isActive ? "live" : needsAttention ? "danger" : ""}`}>
                {loading ? "Checking" : needsAttention ? "Payment due" : isActive ? "Active" : "Inactive"}
              </span>
              <h3>Weekly Auto-Entry</h3>
              <p>
                {isActive
                  ? cancelAtPeriodEnd ? "Cancellation scheduled" : "Renews automatically each week"
                  : needsAttention ? "Update your payment method to continue" : "Not currently subscribed"}
              </p>
              <ul>
                <li><CheckCircle2 size={15} /> One paid ticket request per billing cycle</li>
                <li><CheckCircle2 size={15} /> Automatic assignment to the next open pool</li>
                <li><CheckCircle2 size={15} /> Manage payment methods and cancellation in Stripe</li>
                {(isActive || needsAttention) && (
                  <li><CalendarClock size={15} /> Current period ends {formatDate(currentPeriodEnd)}</li>
                )}
              </ul>

              {loading ? (
                <button disabled><Loader2 size={16} className="spin" /> Checking billing...</button>
              ) : isActive || needsAttention ? (
                <button onClick={openBillingPortal} disabled={working}>
                  {working ? <Loader2 size={16} className="spin" /> : <CreditCard size={16} />}
                  Manage Billing
                </button>
              ) : (
                <button className="subscription-primary" onClick={startSubscription} disabled={working}>
                  {working ? <Loader2 size={16} className="spin" /> : <CreditCard size={16} />}
                  Subscribe - $4 / week
                </button>
              )}
            </article>
          </section>
        </main>
      </div>
    </div>
  );
}
